import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { clientApiService } from '../../../utils/clientApiService';

const ClientForm = ({ onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    initialMinutes: '',
    initialSessions: '',
    email: '',
    address: '',
    membershipType: '',
    birthDate: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Get existing clients for duplicate phone validation
  const existingClients = clientApiService.getAllClients();

  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'fullName':
        if (!value.trim()) {
          newErrors.fullName = 'Full name is required';
        } else if (value.trim().length < 2) {
          newErrors.fullName = 'Name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          newErrors.fullName = 'Name can only contain letters and spaces';
        } else {
          delete newErrors.fullName;
        }
        break;

      case 'mobileNumber':
        const phoneDigits = value.replace(/\D/g, '');
        if (!value.trim()) {
          newErrors.mobileNumber = 'Mobile number is required';
        } else if (!/^\d{10}$/.test(phoneDigits)) {
          newErrors.mobileNumber = 'Phone number must be exactly 10 digits';
        } else if (Array.isArray(existingClients) && existingClients.some(client => client.phone === phoneDigits)) {
          const existingClient = existingClients.find(client => client.phone === phoneDigits);
          newErrors.mobileNumber = `Phone number already exists for ${existingClient.name}`;
        } else {
          delete newErrors.mobileNumber;
        }
        break;

      case 'initialMinutes':
        if (value && (isNaN(value) || parseInt(value) < 0)) {
          newErrors.initialMinutes = 'Initial minutes must be a positive number';
        } else if (value && parseInt(value) > 10000) {
          newErrors.initialMinutes = 'Initial minutes cannot exceed 10,000';
        } else {
          delete newErrors.initialMinutes;
        }
        break;

      case 'initialSessions':
        if (value && (isNaN(value) || parseInt(value) < 0)) {
          newErrors.initialSessions = 'Initial sessions must be a positive number';
        } else if (value && parseInt(value) > 1000) {
          newErrors.initialSessions = 'Initial sessions cannot exceed 1,000';
        } else {
          delete newErrors.initialSessions;
        }
        break;

      case 'email':
        if (value && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
          newErrors.email = 'Invalid email address';
        } else {
          delete newErrors.email;
        }
        break;

      case 'birthDate':
        if (value && isNaN(Date.parse(value))) {
          newErrors.birthDate = 'Invalid date';
        } else {
          delete newErrors.birthDate;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'mobileNumber') {
      formattedValue = value.replace(/\D/g, '').slice(0, 10); // Only allow 10 digits
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));

    if (touched[name]) {
      validateField(name, formattedValue);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {});
    setTouched(allTouched);

    // Validate all fields
    let isValid = true;
    Object.entries(formData).forEach(([key, value]) => {
      if (!validateField(key, value)) isValid = false;
    });

    if (isValid) {
      const today = new Date().toISOString().split('T')[0];
      const clientData = {
        name: formData.fullName.trim(),
        phone: formData.mobileNumber.replace(/\D/g, ''),
        balance: formData.initialMinutes ? parseInt(formData.initialMinutes) : 0,
        sessions: formData.initialSessions ? parseInt(formData.initialSessions) : 0,
        email: formData.email || undefined,
        address: formData.address || undefined,
        membershipType: formData.membershipType || undefined,
        birthDate: formData.birthDate || undefined,
        joinDate: today
      };
      onSubmit(clientData);
    }
  };

  const isFormValid = Object.keys(errors).length === 0 && 
                     formData.fullName.trim() && 
                     formData.mobileNumber.trim();

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-2">
          Create New Client Profile
        </h2>
        <p className="text-muted-foreground">
          Enter client information to create a new profile and set up their solarium minute balance.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-6">
          <Input
            label="Full Name"
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="Enter client's full name"
            error={touched.fullName ? errors.fullName : ''}
            required
            className="transition-all duration-150"
            autoFocus
          />

          <Input
            label="Mobile Number"
            type="tel"
            name="mobileNumber"
            value={formData.mobileNumber}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="Enter 10-digit phone number"
            error={touched.mobileNumber ? errors.mobileNumber : ''}
            required
            className="transition-all duration-150"
            maxLength={10}
          />

          <Input
            label="Initial Minutes (Optional)"
            type="number"
            name="initialMinutes"
            value={formData.initialMinutes}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="0"
            description="Add purchased minutes to the client's account during creation"
            error={touched.initialMinutes ? errors.initialMinutes : ''}
            min="0"
            max="10000"
            className="transition-all duration-150"
          />

          <Input
            label="Initial Sessions (Optional)"
            type="number"
            name="initialSessions"
            value={formData.initialSessions}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="0"
            description="Add purchased sessions to the client's account during creation"
            error={touched.initialSessions ? errors.initialSessions : ''}
            min="0"
            max="1000"
            className="transition-all duration-150"
          />

          <Input
            label="Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="client@email.com"
            error={touched.email ? errors.email : ''}
            className="transition-all duration-150"
          />

          <Input
            label="Address"
            type="text"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="123 Main St, City, State"
            className="transition-all duration-150"
          />

          <Input
            label="Membership Type"
            type="text"
            name="membershipType"
            value={formData.membershipType}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="Standard, Premium, VIP, etc."
            className="transition-all duration-150"
          />

          <Input
            label="Birth Date"
            type="date"
            name="birthDate"
            value={formData.birthDate}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="transition-all duration-150"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-border">
          <Button
            type="submit"
            variant="default"
            size="lg"
            loading={isLoading}
            disabled={!isFormValid || isLoading}
            iconName="UserPlus"
            iconPosition="left"
            iconSize={18}
            className="flex-1 sm:flex-none sm:min-w-[140px] bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-200 hover:shadow-md"
          >
            {isLoading ? 'Creating...' : 'Save Client'}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={onCancel}
            disabled={isLoading}
            iconName="X"
            iconPosition="left"
            iconSize={18}
            className="flex-1 sm:flex-none sm:min-w-[100px] transition-all duration-200"
          >
            Cancel
          </Button>
        </div>
      </form>

      {/* Form Tips */}
      <div className="mt-8 p-4 bg-muted rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={18} className="text-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Quick Tips:</p>
            <ul className="space-y-1">
              <li>• Phone numbers must be unique for each client</li>
              <li>• Initial minutes can be added now or later from the client's profile</li>
              <li>• All client data is saved locally and persists between sessions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientForm;